import { Component, computed, ElementRef, input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { IFormInput, IFormInputGroup } from '@mwe/models';
import { NgbDropdown, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { ButtonComponent } from '../../../buttons/button/button.component';

@Component({
  selector: 'mwe-combobox-select',
  templateUrl: './combobox-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ComboboxSelectComponent,
      multi: true,
    },
  ],
  imports: [NgbDropdown, FormsModule, ReactiveFormsModule, TranslateDirective, NgClass, ButtonComponent, NgbDropdownMenu],
})
export class ComboboxSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  parentForm = input.required<UntypedFormGroup>();
  inputElem = input.required<IFormInputGroup>();
  isLoading = input(false);

  @ViewChild('dropdown') dropdownComponent: NgbDropdown;
  @ViewChild('dropdownElement') dropdownElement: ElementRef<HTMLElement>;
  @ViewChild('searchInputElem') searchInputElement: ElementRef<HTMLInputElement>;
  formControlDirective: FormControlDirective;
  searchInput = new FormControl('');
  searchInputValue = toSignal(this.searchInput.valueChanges);
  pills = signal<IFormInput[]>([]);
  selectedOption = signal<HTMLElement | null>(null);

  filteredOptions = computed(() => {
    const searchValue = this.searchInputValue();
    if (!this.searchInput.value) {
      return this.inputElem().inputs;
    }
    return this.inputElem().inputs.filter(input => {
      return input.labelKey.toLowerCase().includes(searchValue.toLowerCase());
    });
  });

  ngOnInit() {
    this.parentForm().valueChanges.subscribe(values => {
      this.pills.set(
        this.inputElem().inputs.filter(input => {
          return values[input.name] === true;
        }),
      );
    });
    this.inputElem().inputs.forEach(value => {
      if (value.initialValue === 'true' || value.initialValue === true) {
        this.parentForm().controls[value.name].patchValue(true);
      }
    });
    document.body.addEventListener('click', this.clickHandler.bind(this));
  }

  ngOnDestroy() {
    document.body.removeEventListener('click', this.clickHandler.bind(this));
  }

  focusInHandler(e): void {
    if (this.dropdownElement?.nativeElement?.contains(document.activeElement)) {
      /* Focus was already in the container */
      return;
    }
    this.openDropdown();
  }

  focusOutHandler(e): void {
    if (!e.relatedTarget) {
      return;
    }
    if (this.dropdownElement?.nativeElement?.contains(document.activeElement)) {
      /* Focus will still be within the container */
      return;
    }
    this.dropdownComponent.close();
  }

  getUniqueId(i: number): string {
    return `combobox-select-option-${this.inputElem().name}-${i}`;
  }

  registerOnTouched(fn: any): void {
    this.formControlDirective.valueAccessor.registerOnTouched(fn);
  }

  registerOnChange(fn: any): void {
    this.formControlDirective.valueAccessor.registerOnChange(fn);
  }

  writeValue(obj: any): void {
    this.formControlDirective.valueAccessor.writeValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective.valueAccessor.setDisabledState(isDisabled);
  }

  onPillClick(event: MouseEvent, name: string): void {
    event.preventDefault();
    this.parentForm().controls[name].patchValue(false);
    this.searchInputElement?.nativeElement?.focus();
  }

  keyboardNavigation($event: KeyboardEvent): void {
    if (!this.inputElem().inputs.length) {
      return;
    }

    switch ($event.key) {
      case 'Space':
      case 'Enter': {
        if (!this.selectedOption() || !this.inputElem().inputs.length) {
          break;
        }
        this.toggleOption(this.selectedOption());
        $event.preventDefault();
        $event.stopImmediatePropagation();
        break;
      }
      case 'ArrowDown': {
        $event.preventDefault();
        if (!this.selectedOption()) {
          const firstOption = this.dropdownElement?.nativeElement?.querySelector('.dropdown-item') as HTMLElement;
          this.selectOption(firstOption);
          break;
        }
        const nextElement = this.selectedOption().nextSibling as HTMLElement;
        if (
          nextElement &&
          nextElement.nodeType === 1 &&
          this.selectedOption().classList.contains('dropdown-item') &&
          nextElement.classList.contains('dropdown-item')
        ) {
          this.selectOption(nextElement);
        }
        break;
      }
      case 'ArrowUp': {
        $event.preventDefault();
        const prevElement = this.selectedOption().previousSibling as HTMLElement;
        if (
          prevElement &&
          prevElement.nodeType === 1 &&
          this.selectedOption().classList.contains('dropdown-item') &&
          prevElement.classList.contains('dropdown-item')
        ) {
          this.selectOption(prevElement);
        }
        break;
      }
      case 'Escape': {
        this.dropdownComponent.close();
        break;
      }
      default: {
        this.selectOption(null);
        this.openDropdown();
      }
    }
  }

  selectOption(element?: HTMLElement): void {
    if (!element) {
      this.selectedOption.set(null);
      this.dropdownElement?.nativeElement?.querySelectorAll('.dropdown-item')?.forEach(e => {
        e.classList.remove('dropdown-item-selected');
      });
      return;
    }
    const siblings = n => [...n.parentElement.children].filter(c => c.nodeType == 1 && c != n);
    element.classList.add('dropdown-item-selected');
    siblings(element).forEach(e => e.classList.remove('dropdown-item-selected'));
    this.selectedOption.set(element);
  }

  toggleOption(element: HTMLElement): void {
    this.parentForm().controls[element.querySelector('input').getAttribute('name')].patchValue(!element.getAttribute('checked'));
  }

  clickHandler(e: MouseEvent): void {
    this.dropdownComponent.close();
  }

  openDropdown() {
    if (!this.isLoading()) {
      this.dropdownComponent.open();
    }
  }
}
